import { useKirjaustunniste } from "./kirjaustunniste";
import { naytaPvm } from "./pvm";
import { useCallback, useEffect, useState } from "react";
import { useViimeisin } from "./tuoreus";
import { api, ApiError, punchSuljettu, type Punch, type Kayttaja } from "./api";
import { Hoitaja } from "./Hoitaja";
import Liitteet from "./Liitteet";
import { punchinLuokka, punchinTeksti } from "./api";
import { naytaHitsinumero } from "./HitsiTunnus";
import { t, kieli, virheTeksti, type KaannosAvain } from "./kieli";

/* T439: estoluokan nimi ja määritelmä käyttäjän kielellä; tallennettava arvo on kirjain A/B/C. Suomeksi teksti tulee
   api.ts:n PUNCH_LUOKAT-taulusta kuten ennen (punchinLuokka), muilla kielillä käännöksestä. */
const LUOKAN_NIMI: Record<"A" | "B" | "C", KaannosAvain> = { A: "pc.luokka.A", B: "pc.luokka.B", C: "pc.luokka.C" };
const LUOKAN_MAARITELMA: Record<"A" | "B" | "C", KaannosAvain> = { A: "pc.maar.A", B: "pc.maar.B", C: "pc.maar.C" };
export const luokanNimi = (l: string, fi: string) => (kieli() === "fi" || !(l in LUOKAN_NIMI) ? fi : t(LUOKAN_NIMI[l as "A" | "B" | "C"]));
export const luokanMaar = (l: string, fi: string) => (kieli() === "fi" || !(l in LUOKAN_MAARITELMA) ? fi : t(LUOKAN_MAARITELMA[l as "A" | "B" | "C"]));

/* ═══════════════════════════════════════════════════════════
   Punch — linjan tarkastuslöydökset.

   Rivi per punch: yksitellen kuitattava (Korjattu). Kirjaaja ja
   päivä täyttyvät kirjautuneesta, mutta ovat muokattavissa — punch
   voidaan tehdä paperilla ja kirjata vasta seuraavana päivänä.
   Kuittaus ei poista riviä: avoimet JA korjatut säilyvät.
   ═══════════════════════════════════════════════════════════ */

export default function Punchit({
  linjaId, saaKirjata, onMuutos, saaPoistaaLiitteen = false, kayttaja,
}: {
  linjaId: number;
  /** 236 (T534): hoitajan napit (otto, osoitus, vapautus) — ilman tunnusta näytetään vain nimi. */
  kayttaja?: Kayttaja;
  saaKirjata: boolean;
  /** DELETE /api/liitteet/:id on tiukempi kuin lisäys. */
  saaPoistaaLiitteen?: boolean;
  onMuutos?: () => void;
}) {
  // Toni 17.10.2026 klo 07.40 kohta 4: kenttätekijä voi olla hoitaja ja lisätä kuvan hoitamaansa punchiin; kuittaus ei kuulu hänelle.
  const kenttatekija = !!kayttaja?.roolit.includes("kenttatekija");
  const [lista, setLista] = useState<Punch[] | null>(null);
  /* Punchin kuvat = liite jonka kohde_taulu = 'punch'. Yksi mekanismi. */
  const [kuvatAuki, setKuvatAuki] = useState<number | null>(null);
  const [virhe, setVirhe] = useState<string | null>(null);
  const [vika, setVika] = useState("");
  const [huomioinut, setHuomioinut] = useState("");
  const [pvm, setPvm] = useState("");
  const [luokka, setLuokka] = useState<"" | "A" | "B" | "C">("");
  const [vastuu, setVastuu] = useState("");   // T525: kuten isometrinastassa
  const [muokattava, setMuokattava] = useState<number | null>(null);
  const [tunniste, vaihdaTunniste] = useKirjaustunniste();   // T323

  const uusi = useViimeisin();   // päätös 99
  const lataa = useCallback(async () => {
    const tuore = uusi();
    try { const l = await api.punchit(linjaId); if (!tuore()) return; setLista(l); setVirhe(null); }
    catch (e) { if (!tuore()) return; setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("pc.lataus_virhe")); setLista([]); }
  }, [linjaId, uusi]);
  useEffect(() => { void lataa(); }, [lataa]);

  const paivita = async () => { await lataa(); onMuutos?.(); };

  async function lisaa() {
    if (!vika.trim()) { setVirhe(t("pc.kuvaa_vika")); return; }
    // Estoluokka pakollinen (Toni 26.9.2026): mitattu ennen, oletusvalinta «luokka —» tallensi luokattoman.
    if (!luokka) { setVirhe(t("pc.valitse_luokka_virhe")); return; }
    try {
      await api.lisaaPunch(linjaId, {
        kirjaustunniste: tunniste,
        vika: vika.trim(), luokka, ...(vastuu.trim() && { vastuu: vastuu.trim() }),
        ...(huomioinut.trim() && { huomioinut: huomioinut.trim() }),
        ...(pvm && { huomio_pvm: pvm }),
      });
      vaihdaTunniste();
      setVika(""); setHuomioinut(""); setPvm(""); setVastuu(""); setVirhe(null);
      await paivita();
    } catch (e) { setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("qr.lisays_virhe")); }
  }

  const avoimet = (lista ?? []).filter((p) => !punchSuljettu(p));
  const korjatut = (lista ?? []).filter((p) => punchSuljettu(p));

  return (
    <div className="punch">
      <div className="punch__otsikko">
        {/* Osion otsikko kuten Linjatarkastus (Toni 25.10.2026: «otsikot on himmeenä»). */}
        <h3 style={{ margin: 0 }} data-osio-otsikko="">{t("pc.otsikko")}</h3>
        {avoimet.length > 0 && <span className="punch__avoimet">{t("pc.n_avoinna", { n: avoimet.length })}</span>}
      </div>
      {virhe && <p className="huom" role="alert">{virhe}</p>}
      {lista === null && <p className="dim">{t("yleinen.ladataan")}</p>}
      {lista && lista.length === 0 && <p className="dim">{t("pc.ei_puncheja")}</p>}

      {avoimet.length > 0 && (
        <ul className="punch__lista">
          {avoimet.map((p) => (
            <li key={p.id} className="punch__rivi">
              {muokattava === p.id
                ? <PunchMuokkaus p={p} valmis={async () => { setMuokattava(null); await paivita(); }} />
                : (
                  <>
                    <span className="punch__vika">
                      {(() => { const lk = punchinLuokka(p.luokka); return lk
                        ? <span className={lk.tunnus === "A" ? "merkki merkki--vika" : "merkki"} title={luokanMaar(lk.tunnus, lk.maaritelma)} style={{ marginRight: 6 }}>{lk.tunnus} · {luokanNimi(lk.tunnus, lk.nimi)}</span>
                        : null; })()}
                      {punchinTeksti(p)}
                      {naytaHitsinumero(p.hitsinumero) && <span className="dim"> · {t("pc.hitsi_pieni")} {naytaHitsinumero(p.hitsinumero)}</span>}
                      {/* 249: linjatarkastuksen kohta, johon punch kirjattiin */}
                      {p.linjatarkastus_kohta && <span className="dim" data-punch-kohta={p.linjatarkastus_kohta}> · {t("ltar.otsikko")} {p.linjatarkastus_kohta}</span>}
                      {p.isantajarjestelma === "sharepoint" && <span className="dim" > · SP</span>}
                      {/* Toni 17.10.2026 klo 07.40 kohta 2: vastuu (kuka korjaa) näkyviin rivillä. */}
                      {p.vastuu && <span className="dim" data-punch-vastuu-nakyy={p.id}> · {t("pc.vastuu_lyhyt")}: {p.vastuu}</span>}
                    </span>
                    <span className="dim punch__meta">
                      {p.huomioinut ?? "—"}{p.huomio_pvm ? ` · ${naytaPvm(p.huomio_pvm)}` : ""}
                      {" · "}
                      <button className="nappi nappi--kevyt" onClick={() => setKuvatAuki(kuvatAuki === p.id ? null : p.id)}
                              title={t("pc.kuvat_title")}>
                        {kuvatAuki === p.id ? t("pc.sulje_kuvat") : (p.liitteita ? t("hl.kuvat_n", { n: p.liitteita }) : (saaKirjata || (kenttatekija && p.hoitaja?.id === kayttaja?.id) ? t("hl.lisaa_kuva") : t("pc.ei_kuvia")))}
                      </button>
                      {/* 236 (T534): hoitaja meta-rivin jatkona — ei omaa riviä (Toni 13.10.2026: rivi ei kasva). */}
                      {/* Toni 17.10.2026 klo 07.40 kohta 4: kenttätekijä saa olla punchin hoitaja (ottaa itselleen tai saa osoitettuna). */}
                      {kayttaja && <>{" · "}<Hoitaja taulu="punch" id={p.id} hoitaja={p.hoitaja} kayttaja={kayttaja} inline
                                          saaKirjoittaa={saaKirjata || kenttatekija} muuttui={() => void paivita()} /></>}
                    </span>
                    {kuvatAuki === p.id && (
                      <div style={{ flexBasis: "100%", marginTop: 6 }}>
                        <Liitteet pienoiskuvat kohdeTaulu="punch" kohdeId={p.id} saaMuokata={saaKirjata || (kenttatekija && p.hoitaja?.id === kayttaja?.id)}
                                  saaPoistaa={saaPoistaaLiitteen} otsikko={t("pu.kuvat")} muuttui={paivita} />
                      </div>
                    )}
                    {saaKirjata && (
                      <span className="punch__napit">
                        <button className="nappi nappi--paa" onClick={async () => { await api.kuittaaPunch(p.id); await paivita(); }}>{t("pc.korjattu")}</button>
                        <button className="nappi nappi--kevyt" onClick={() => setMuokattava(p.id)}>{t("qr.muokkaa")}</button>
                        <button className="punch__poista" title={t("tk.poista")} onClick={async () => { await api.poistaPunch(p.id); await paivita(); }}>×</button>
                      </span>
                    )}
                  </>
                )}
            </li>
          ))}
        </ul>
      )}

      {saaKirjata && (
        <div className="punch__lisays">
          <input className="punch__vikakentta" value={vika} placeholder={t("pc.mika_vika")}
                 onChange={(e) => setVika(e.target.value)} />
          <input value={huomioinut} placeholder={t("pc.huomioinut_oletus")} style={{ width: 150 }}
                 onChange={(e) => setHuomioinut(e.target.value)} />
          <input type="date" value={pvm} title={t("pc.huomiopaiva")}
                 onChange={(e) => setPvm(e.target.value)} />
          {/* Estoluokka: määritelmä on valinnan vieressä, koska valitsija on
              kentällä eikä lue ohjetta. PAKOLLINEN (Toni 26.9.2026): tyhjä oletus ei tallennu,
              vaan lomake sanoo mitä puuttuu. */}
          <select value={luokka} onChange={(e) => setLuokka(e.target.value as "" | "A" | "B" | "C")}
                  title={t("pc.luokka_title")}>
            <option value="" disabled>{t("pc.valitse_luokka")}</option>
            {(["A", "B", "C"] as const).map((l) => <option key={l} value={l}>{l} · {luokanNimi(l, punchinLuokka(l)?.nimi ?? l)}</option>)}
          </select>
          <input value={vastuu} placeholder={t("pc.vastuu")} style={{ width: 150 }} maxLength={120}
                 onChange={(e) => setVastuu(e.target.value)} data-punch-vastuu="" />
          <button className="nappi nappi--paa" onClick={lisaa} data-punch-lisaa="">{t("pc.lisaa")}</button>
          {/* Toni 13.10.2026: vastuu kielestä on kirjoittajalla — ohje vikakentän alle käyttäjän kielellä. */}
          <p className="huom" data-kirjoitusohje="" style={{ flexBasis: "100%", margin: "4px 0 0" }}>{t("kirjoitusohje.korjaava")}</p>
        </div>
      )}

      {korjatut.length > 0 && (
        <>
          <div className="punch__valiotsikko dim">{t("pc.korjatut", { n: korjatut.length })}</div>
          <ul className="punch__lista punch__lista--korjatut">
            {korjatut.map((p) => (
              <li key={p.id} className="punch__rivi punch__rivi--korjattu">
                <span className="punch__vika">{p.vika}</span>
                <span className="dim punch__meta">
                  {t("pc.korjattu_pieni")} {p.kuitannut ?? "—"}{p.kuittaus_pvm ? ` · ${naytaPvm(p.kuittaus_pvm)}` : ""}
                </span>
                {saaKirjata && (
                  <span className="punch__napit">
                    <button className="nappi nappi--kevyt"
                            onClick={async () => { await api.muokkaaPunch(p.id, { kuitannut: null, kuittaus_pvm: null }); await paivita(); }}>{t("pc.avaa")}</button>
                  </span>
                )}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

/* Avoimen punchin muokkaus: vika, huomioinut, huomiopäivä (paperikirjaus) ja estoluokka.
   Luokka tänne 26.9.2026: SharePointista tuodut luokattomat (pekilo 86) voi luokitella vain täältä, eikä
   luokkaa voi tyhjentää — palvelin hylkää tyhjän. */
function PunchMuokkaus({ p, valmis }: { p: Punch; valmis: () => Promise<void> }) {
  const [vika, setVika] = useState(p.vika);
  const [huomioinut, setHuomioinut] = useState(p.huomioinut ?? "");
  const [pvm, setPvm] = useState(p.huomio_pvm ?? "");
  const [luokka, setLuokka] = useState<"" | "A" | "B" | "C">(
    (["A", "B", "C"] as const).find((l) => l === (p.luokka ?? "").trim().toUpperCase()) ?? "");
  const tallenna = async () => {
    await api.muokkaaPunch(p.id, {
      vika: vika.trim() || p.vika,
      huomioinut: huomioinut.trim() || null,
      huomio_pvm: pvm || null,
      ...(luokka && { luokka }),
    });
    await valmis();
  };
  return (
    <span className="punch__muokkaus">
      <input value={vika} onChange={(e) => setVika(e.target.value)} className="punch__vikakentta" />
      <input value={huomioinut} placeholder={t("pc.huomioinut")} style={{ width: 130 }}
             onChange={(e) => setHuomioinut(e.target.value)} />
      <input type="date" value={pvm} onChange={(e) => setPvm(e.target.value)} />
      <select value={luokka} onChange={(e) => setLuokka(e.target.value as "" | "A" | "B" | "C")}
              title={t("pc.luokka_title")}>
        <option value="" disabled>{t("pc.valitse_luokka")}</option>
        {(["A", "B", "C"] as const).map((l) => <option key={l} value={l}>{l} · {luokanNimi(l, punchinLuokka(l)?.nimi ?? l)}</option>)}
      </select>
      <button className="nappi nappi--paa" onClick={tallenna}>{t("pc.tallenna")}</button>
      <button className="nappi nappi--kevyt" onClick={() => void valmis()}>{t("hl.peruuta")}</button>
    </span>
  );
}
